import { useState } from 'react'
import { toast } from 'react-toastify'
import { previewDataModelSchemaResource } from 'services/kraken-core/data_model/dataModel.service'
import { isEditableContentType } from 'common/helpers/contentType'

interface UseResourceEditProps {
  onEditComplete?: () => void
}

export const useResourceEdit = ({ onEditComplete }: UseResourceEditProps = {}) => {
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [selectedResource, setSelectedResource] = useState<any>(null)
  const [isLoadingContent, setIsLoadingContent] = useState(false)

  const handleEditResource = async (resource: any) => {
    setSelectedResource(resource)
    setIsEditDialogOpen(true)

    // Only call preview endpoint for text-based content types
    const isEditable = isEditableContentType(resource.content_type)

    if (isEditable) {
      setIsLoadingContent(true)

      try {
        const { response } = await previewDataModelSchemaResource(
          resource.id || resource.resourceId
        )

        if (response?.data) {
          setSelectedResource(prev => ({
            ...prev,
            content: response.data.content,
            content_type: response.data.content_type,
            contentType: response.data.content_type, // For compatibility with ValidationFile interface
          }))
        }
      } catch (error) {
        toast.error('Failed to load resource content')
        setSelectedResource(null)
        setIsEditDialogOpen(false)
      } finally {
        setIsLoadingContent(false)
      }
    } else {
      // For non-editable files, just use the existing resource data
      // Set empty content since we won't be editing it
      setSelectedResource(prev => ({
        ...prev,
        content: '',
        content_type: resource.content_type,
        contentType: resource.content_type,
      }))
    }
  }

  const closeEditDialog = () => {
    setIsEditDialogOpen(false)
    setSelectedResource(null)
  }

  const handleSave = () => {
    closeEditDialog()
    if (onEditComplete) {
      onEditComplete()
    }
  }

  return {
    isEditDialogOpen,
    selectedResource,
    isLoadingContent,
    handleEditResource,
    closeEditDialog,
    handleSave,
  }
}
